import { z } from 'zod';
import { PublicMedia } from './media.js';
import { Cuid2, IsoDateTime, MoneyCents, Slug } from './primitives.js';
/**
 * Editorial content: articles, partner offers, the monthly competition and
 * ad banners. All of it is written in the back office and read by members.
 *
 * Every image here is a `PublicMedia`, so it is a Cloudinary publicId and
 * dimensions, never a URL (D33, ADR 0006).
 */
/** Home tips and news (requirements.md, News). */
export const Article = z.object({
    id: Cuid2,
    slug: Slug,
    title: z.string().min(1).max(160),
    /** One or two sentences for the card and the meta description. */
    excerpt: z.string().max(320),
    /** Markdown. Rendered server-side; the feed shows the excerpt only. */
    body: z.string(),
    cover: PublicMedia.nullable(),
    /** "MyHome+" unless a guest wrote it. */
    author: z.string().max(120),
    readMinutes: z.int().positive().max(60),
    publishedAt: IsoDateTime,
});
/**
 * How a member claims a partner offer. The rotating card code is one route
 * (D12, O15); a plain code or a link are the others.
 */
export const OFFER_REDEMPTIONS = ['CARD_CODE', 'PROMO_CODE', 'LINK', 'IN_STORE'];
/** A partner's member benefit (requirements.md, Rewards). */
export const Offer = z.object({
    id: Cuid2,
    slug: Slug,
    partnerName: z.string().min(1).max(120),
    title: z.string().min(1).max(160),
    description: z.string().max(2000),
    image: PublicMedia.nullable(),
    redemption: z.enum(OFFER_REDEMPTIONS),
    /** Set when redemption is PROMO_CODE. */
    promoCode: z.string().max(60).nullable(),
    /** Set when redemption is LINK. */
    url: z.url().nullable(),
    terms: z.string().max(4000).nullable(),
    /** Null means it runs until withdrawn. */
    endsAt: IsoDateTime.nullable(),
});
/**
 * The monthly competition (requirements.md). Entry rules are governed by the
 * CPA, so the prize, the closing date and a link to the rules travel with it
 * rather than being written into a screen.
 */
export const Competition = z.object({
    id: Cuid2,
    slug: Slug,
    title: z.string().min(1).max(160),
    prize: z.string().min(1).max(200),
    description: z.string().max(2000),
    image: PublicMedia.nullable(),
    /** When entries close. The card counts down to it. */
    closesAt: IsoDateTime,
    /** When the winner is drawn, which is not always the closing date. */
    drawnAt: IsoDateTime.nullable(),
    /** Whether a paying member is entered without doing anything. See the competition rules page. */
    automaticEntry: z.boolean(),
    /** The published rules, under /legal/competition-rules. */
    rulesPath: z.string().startsWith('/'),
});
/** Where a paid banner may appear. The dashboard already labels its slot "Sponsored". */
export const AD_PLACEMENTS = ['FEED', 'DASHBOARD', 'DIRECTORY', 'ARTICLE'];
/**
 * A paid placement. Always labelled "Sponsored" wherever it renders
 * (DESIGN.md), so a member is never asked to guess what they are looking at.
 */
export const AdBanner = z.object({
    id: Cuid2,
    advertiserName: z.string().min(1).max(120),
    image: PublicMedia,
    /** Where tapping it goes. External, so screens open it in a new tab with rel=noopener. */
    url: z.url(),
    placement: z.enum(AD_PLACEMENTS),
    startsAt: IsoDateTime,
    endsAt: IsoDateTime.nullable(),
});
/**
 * The numbers the About page shows (requirements.md, About us).
 *
 * Real counts only. A screen renders this or it renders nothing: showing
 * zeros before the endpoint exists reads as "nobody uses this".
 */
export const PublicStats = z.object({
    /** Members with an entitled subscription. */
    membersCount: z.int().nonnegative(),
    /** Total member savings this calendar year, in cents (integer cents, ZAR). */
    savingsThisYearCents: MoneyCents,
    /** These are cached; this says how stale the number may be. */
    updatedAt: IsoDateTime,
});
//# sourceMappingURL=content.js.map